// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API endpoint for retrieving event type configurations.
 * Provides functionality to fetch the available event types and their
 * associated properties, which define the structure and validation rules
 * for creating new events.
 */
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { eventConfigurationSchema } from '@/asset/api/types';
import { z } from 'zod';

/**
 * Schema for validating the API response when fetching event configurations.
 * Transforms the response to provide a more intuitive structure with
 * the configurations array separated from metadata. Handles null responses
 * by providing an empty array for consistent processing.
 */
const eventConfigurationsResponseSchema = z
  .object({
    data: z.array(eventConfigurationSchema).nullable(),
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({
    ...response,
    eventConfigurations: data ?? [],
  }));

/**
 * Retrieves all available event type configurations.
 * This function handles the API call to fetch the complete list of
 * event types and their properties, validating the response data
 * using Zod schemas. These configurations define what types of events
 * can be created and what properties they require.
 *
 * @returns Promise resolving to the list of event configurations and request metadata
 * @throws Will throw an error if the API request fails or if the response data is invalid
 */
export async function getEventConfigurations() {
  const url = '/v1/event-configuration';
  const response = await api.get(url);

  return parseResponse(eventConfigurationsResponseSchema, response.data, {
    schemaName: 'EventConfigurations',
    endpoint: url,
  });
}
